import { isBefore, parseISO } from 'date-fns'
import {
  array,
  boolean,
  enum_,
  forward,
  minLength,
  nullable,
  number,
  object,
  partialCheck,
  picklist,
  pipe,
  string,
  type InferOutput
} from 'valibot'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'
import type { ToursExEntity } from '@/libs/api'
import { PARTNER_TIER } from '@/libs/api/types'
import { nullableDateToStringSchema } from '@/libs/valibot'

import { mapToToursChargeAdditionalChargeFormValue } from './tours-charge-additional-charges-form-value'
import {
  mapToToursChargeAdditionalPaymentFormValue,
  buildToursChargeAdditionalPaymentFormValueSchema
} from './tours-charge-additional-payments-form-value'
import {
  buildToursChargeCustomerChargeFormValueSchema,
  type ToursChargeCustomerChargeFormValue
} from './tours-charge-customer-charge-form-value'
import {
  mapToToursRouteFormValue,
  toursRouteFormValueSchema
} from './tours-route-form-value'

// 端数処理 (roundType)。付帯費用 (shipment-order-additional-form-value) の RoundTypePicklist と同一定義。
const RoundTypePicklist = picklist(['ROUND_UP', 'ROUND_DOWN', 'ROUND_HALF_UP'])

/**
 * 運行実績詳細ページのフォームスキーマを生成する。
 *
 * VOC_PJ-941 #3984: 税区分・端数処理の必須チェック（払い・請求・付帯）は
 * `requireTaxSetting` で制御する（定義は 1 箇所、判定だけ生成時の値を参照）。
 * - 通常編集: true（ブランクにした時点でフィールド脇に必須エラー）
 * - キャンセル編集: false（金額 0 初期化で送るため必須にしない = BE ガードの isCanceling 除外と対応）
 * 使い分けは useTourDetailPageForm の resolver 切り替えで行う。
 */
export const buildTourDetailPageFormValueSchema = ({
  requireTaxSetting
}: {
  requireTaxSetting: boolean
}) =>
  pipe(
    object({
      /** 修正理由 */
      editReason: pipe(
        string(),
        minLength(1, VALIDATION_MESSAGES.REQUIRED_ITEM)
      ),
      /** 業務開始日時 */
      startAt: string(),
      /** 業務終了日時 */
      endAt: string(),
      /** 配送ルート */
      details: array(toursRouteFormValueSchema),
      /** 距離(km) */
      distance: nullable(number()),
      /** 合計運賃（契約種別がエラーの場合のみ入力可） */
      chargeTotalPrice: nullable(number()),
      /**
       * 基本支払額(Charge)の税区分マスタ ID（#3328 VOC_PJ-761、案件個別オーバーライド）。
       * `null` のときは BE 側でカスケード fallback（取引先 default → 課税10%）。
       */
      chargeTaxCategoryId: nullable(number()),
      /**
       * 基本支払額(Charge)の内税フラグ（VOC_PJ-844 #3624、案件個別オーバーライド）。
       * `=== true` のときのみ内税。`null` のときは BE/月次カスケード fallback（取引先 > 拠点 → 外税）。
       */
      chargeIsTaxInclusive: nullable(boolean()),
      /**
       * 基本支払額(Charge)の端数処理（VOC_PJ-888 #3917、案件個別オーバーライド）。
       * `null` のときは BE/マスタカスケード fallback（取引先 > 拠点）。
       */
      chargeRoundType: nullable(RoundTypePicklist),
      /** 証跡ファイル */
      tourEvidenceFiles: array(object({ key: string(), name: string() })),
      /** 追加支払運賃 */
      additionalPayments: array(
        buildToursChargeAdditionalPaymentFormValueSchema({ requireTaxSetting })
      ),
      /** 払い計上日 (VOC_PJ-55 T-3、Tour 全体に 1 件、Charge.operatingDate) */
      chargeOperatingDate: nullableDateToStringSchema,
      /** 請求額 */
      customerCharges: array(
        buildToursChargeCustomerChargeFormValueSchema({ requireTaxSetting })
      ),
      /** 車両 */
      truckId: nullable(number()),
      /** 備考 */
      note: nullable(string()),
      /** キャンセル処理中フラグ（API送信用） */
      isCanceling: nullable(boolean()),
      /** 契約種別がエラーか区別するためのフラグ */
      isErrorStatus: nullable(boolean()),
      /** ドライバーID */
      driverUserId: nullable(number()),
      /** ドライバー名 */
      driverName: nullable(string()),
      /** 請負階層 */
      partnerTier: nullable(enum_(PARTNER_TIER)),
      /** 実運送会社 */
      subPartnerId: nullable(number())
    }),
    forward(
      partialCheck(
        [['startAt'], ['endAt']],
        ({ startAt, endAt }) => isBefore(parseISO(startAt), parseISO(endAt)),
        VALIDATION_MESSAGES.AFTER_START_DATE
      ),
      ['endAt']
    ),
    forward(
      partialCheck(
        [['additionalPayments'], ['chargeTotalPrice'], ['isErrorStatus']],
        ({ additionalPayments, chargeTotalPrice, isErrorStatus }) => {
          const hasAdditionalPayments = additionalPayments.length > 0
          const isChargeTotalPriceMissing = chargeTotalPrice == null

          if (
            isErrorStatus &&
            hasAdditionalPayments &&
            isChargeTotalPriceMissing
          ) {
            return false
          }

          return true
        },
        VALIDATION_MESSAGES.ADDITIONAL_FARES_INPUT_TOTAL_FARE
      ),
      ['chargeTotalPrice']
    ),
    // VOC_PJ-55 T-3: 払い計上日は必須。ブランクのまま保存すると BE 側で startAt 由来に
    // フォールバックされて勝手に値が入ってしまうため、FE 側で必須化して保存を防ぐ。
    forward(
      partialCheck(
        [['chargeOperatingDate']],
        ({ chargeOperatingDate }) =>
          chargeOperatingDate !== null && chargeOperatingDate !== '',
        VALIDATION_MESSAGES.REQUIRED_ITEM
      ),
      ['chargeOperatingDate']
    ),
    // VOC_PJ-55 T-3: 請求計上日 (SO 単位) は SO がある場合のみ必須。
    // SO 0 件 (= CustomerCharge なし) の Tour は運行実績追加経路で許容されているため
    // (= DB 実態 566 件中 23 件、4.1%)、空配列はチェックをスキップする。
    // 加えて every() は空配列で常に true を返す仕様のため、length === 0 条件を明示する。
    forward(
      partialCheck(
        [['customerCharges']],
        ({ customerCharges }) =>
          customerCharges.length === 0 ||
          customerCharges.every(
            (cc) => cc.operatingDate !== null && cc.operatingDate !== ''
          ),
        VALIDATION_MESSAGES.REQUIRED_ITEM
      ),
      ['customerCharges']
    ),
    // VOC_PJ-941 #3984 → #4556: 基本支払額の税区分・端数処理は金額の有無に関わらず必須。
    // 内税フラグは対象外。キャンセル編集 (requireTaxSetting: false) のみ除外。
    // 請求・付帯の必須チェックは各行スキーマ (build* ファクトリ) 側に requireTaxSetting 連動で
    // 定義されており、フィールド脇にエラーが表示される。
    forward(
      partialCheck(
        [['chargeTaxCategoryId']],
        ({ chargeTaxCategoryId }) =>
          !requireTaxSetting || chargeTaxCategoryId != null,
        VALIDATION_MESSAGES.REQUIRED_ITEM
      ),
      ['chargeTaxCategoryId']
    ),
    forward(
      partialCheck(
        [['chargeRoundType']],
        ({ chargeRoundType }) => !requireTaxSetting || chargeRoundType != null,
        VALIDATION_MESSAGES.REQUIRED_ITEM
      ),
      ['chargeRoundType']
    )
  )

/**
 * 運行実績詳細ページのフォームスキーマ（通常編集用 = 税設定の必須チェックあり）。
 * キャンセル編集時は useTourDetailPageForm が requireTaxSetting: false 版へ切り替える。
 */
export const tourDetailPageFormValueSchema = buildTourDetailPageFormValueSchema(
  { requireTaxSetting: true }
)

/**
 * 運行実績詳細ページのフォーム値
 * 計上日フィールドは Date | null を扱う (= 既存 customer-contract 等と同パターン)
 */
export type TourDetailPageFormValue = Omit<
  InferOutput<typeof tourDetailPageFormValueSchema>,
  'chargeOperatingDate' | 'customerCharges'
> & {
  chargeOperatingDate: Date | null
  customerCharges: ToursChargeCustomerChargeFormValue[]
}

/**
 * 運行実績（ToursExEntity）から運行実績詳細ページのフォーム値に変換
 * @param tour 運行実績エンティティ
 * @param isCanceling キャンセル編集中フラグ（trueの場合、運賃を0で初期化、かつフォーム値にもisCancelingを設定）
 */
export const mapToTourDetailPageFormValue = (
  tour: ToursExEntity,
  isCanceling: boolean,
  isErrorStatus: boolean
): TourDetailPageFormValue => {
  const tourDetailFormValue = {
    // 修正理由は空文字で初期化 (キャンセル時はハンドラで別途設定)
    editReason: '',
    distance: tour.distance,
    startAt: tour.startAt,
    endAt: tour.endAt,
    tourEvidenceFiles: tour.tourEvidenceFiles.map((file) => ({
      key: file.tourEvidenceFileKey,
      name: file.tourEvidenceFileName
    })),
    chargeTotalPrice:
      // キャンセル編集中の場合は 0 で初期化。
      // VOC_PJ-910 #4125: 編集時に現在の基本支払額をプリフィルし、運行建/タリフ/エラー契約でも
      // 合計運賃を編集できるようにする（車建は編集対象外だが表示用途のみで無害）。
      // VOC_PJ-941 #3984: 内税明細の税込復元は BE 応答の出口 (CF-4) で行われるため、
      // basePrice には内税なら税込復元済みの値が入っている。保存時に BE が再分解して冪等。
      isCanceling ? 0 : (tour.charge?.basePrice ?? null),
    truckId: tour.truckId,
    note: tour.note ?? null,
    isCanceling: isCanceling,
    driverUserId: tour.driverUserId,
    driverName: tour.driverName,
    partnerTier: tour.partnerTier,
    subPartnerId: tour.subPartnerId,
    // VOC_PJ-55 T-3: 払い計上日。ISO 文字列を Date に変換 (= 既存 endAt パターン)
    chargeOperatingDate: tour.charge?.operatingDate
      ? new Date(tour.charge.operatingDate)
      : null,
    // #3328 VOC_PJ-761: 基本支払額(Charge)の税区分（案件個別オーバーライド）
    chargeTaxCategoryId: tour.charge?.taxCategoryId ?? null,
    // VOC_PJ-844 #3624: 基本支払額(Charge)の内税フラグ（案件個別オーバーライド）
    chargeIsTaxInclusive: tour.charge?.isTaxInclusive ?? null,
    // VOC_PJ-888 #3917: 基本支払額(Charge)の端数処理（案件個別オーバーライド）
    chargeRoundType: tour.charge?.roundType ?? null
  }

  return {
    ...tourDetailFormValue,
    details: tour.details.map(mapToToursRouteFormValue),
    additionalPayments: tour.charge
      ? tour.charge.additionalPayments
          .map(mapToToursChargeAdditionalPaymentFormValue)
          // VOC_PJ-938 #4418: キャンセル編集中は基本運賃と同様に追加支払運賃も 0 円にする
          // (amount が null の行 = INCLUDED/#3890 AMOUNT 保留 は null 維持)。解除時に BE が prev から従前額を復元する。
          .map((p) =>
            isCanceling ? { ...p, amount: p.amount == null ? null : 0 } : p
          )
      : [],
    // パートナーユーザーの応答には請求側（customerCharges）が無い
    customerCharges: (tour.customerCharges ?? []).map((x) => ({
      customerChargeId: x.id,
      // VOC_PJ-941 #3984: 内税明細の税込復元は BE 応答の出口 (CF-4) で行われるため、
      // billedPrice には内税なら税込復元済みの値が入っている。
      // 未入力 (null) は 0 に化けさせず null のまま保持する
      // （未入力と 0 を区別し、未入力なら税設定不要にするため）。キャンセル時のみ 0。
      billedPrice: isCanceling ? 0 : (x.billedPrice ?? null),
      // #3328 VOC_PJ-761: 基本請求額の税区分（案件個別オーバーライド）
      taxCategoryId: x.taxCategoryId ?? null,
      // VOC_PJ-844 #3624: 基本請求額の内税フラグ（案件個別オーバーライド）
      isTaxInclusive: x.isTaxInclusive ?? null,
      // VOC_PJ-888 #3917: 基本請求額の端数処理（案件個別オーバーライド）
      roundType: x.roundType ?? null,
      otherBilledPrice: isCanceling ? 0 : (x.otherBilledPrice ?? 0),
      // #3328 VOC_PJ-761: その他請求額の税区分（運行実績側のみ）
      otherBilledTaxCategoryId: x.otherBilledTaxCategoryId ?? null,
      additionalCharges: x.additionalCharges
        .map((x) => mapToToursChargeAdditionalChargeFormValue(x))
        // VOC_PJ-938 #4418: キャンセル編集中は基本運賃と同様に追加請求料金も 0 円にする
        // (amount が null の行 = INCLUDED/#3890 AMOUNT 保留 は null 維持)。解除時に BE が prev から従前額を復元する。
        .map((c) =>
          isCanceling ? { ...c, amount: c.amount == null ? null : 0 } : c
        ),
      customerContractId: x.customerContract?.id ?? null,
      truckModelId: x.truckModelId,
      totalWeight: x.totalWeight,
      // VOC_PJ-55 T-3: 請求計上日。ISO 文字列を Date に変換
      operatingDate: x.operatingDate ? new Date(x.operatingDate) : null,
      // VOC_PJ-960 #4487: 実績化時コピー済みの customKey1〜5 を編集フォーム初期値へ
      customKey1: x.customKey1 ?? null,
      customKey2: x.customKey2 ?? null,
      customKey3: x.customKey3 ?? null,
      customKey4: x.customKey4 ?? null,
      customKey5: x.customKey5 ?? null
    })),
    isErrorStatus
  }
}
